import { useState, useRef, useEffect, useMemo } from 'react';
import { Send, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getConversations, getMessages, sendMessage } from '@/services/consumerApi';
import { SkeletonCard } from '@/components/common/SkeletonCard';

const avatarUrl = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=e0e7ff&color=4f46e5&size=80`;

export default function Messages() {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState(null);
  const [showThreadMobile, setShowThreadMobile] = useState(false);
  const [draft, setDraft] = useState('');
  const messagesEndRef = useRef(null);

  const { data: convsData, isLoading: convsLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: getConversations,
    refetchInterval: 10000,
  });
  const conversations = useMemo(() => convsData?.results || [], [convsData]);

  useEffect(() => {
    if (!activeId && conversations.length > 0) {
      setActiveId(conversations[0].id);
    }
  }, [conversations, activeId]);

  const { data: msgsData, isLoading: msgsLoading } = useQuery({
    queryKey: ['messages', activeId],
    queryFn: () => getMessages(activeId),
    enabled: !!activeId,
    refetchInterval: 10000,
  });
  const messages = msgsData?.results || [];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const sendMut = useMutation({
    mutationFn: ({ convId, body }) => sendMessage(convId, body),
    onSuccess: (newMsg) => {
      setDraft('');
      qc.setQueryData(['messages', activeId], (old) => ({
        ...old,
        results: [...(old?.results || []), newMsg],
      }));
    },
    onError: () => toast.error('Failed to send message. Your draft was kept.'),
  });

  const handleSend = (e) => {
    e.preventDefault();
    if (!draft.trim() || !activeId || sendMut.isPending) return;
    sendMut.mutate({ convId: activeId, body: draft.trim() });
  };

  const active = conversations.find((c) => c.id === activeId);

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-slate-900">Messages</h1>
      <p className="text-sm text-slate-600">Your conversations with agents.</p>
      <div className="mt-6 grid overflow-hidden rounded-2xl border border-slate-200 bg-white md:grid-cols-[300px_1fr]" style={{ height: '540px' }} data-testid="messages-inbox">
        {/* Conversation list */}
        <div className={cn('overflow-y-auto border-r border-slate-200', showThreadMobile && 'hidden md:block')}>
          {convsLoading ? (
            <div className="space-y-2 p-3">{[1, 2, 3].map((i) => <SkeletonCard key={i} />)}</div>
          ) : conversations.length === 0 ? (
            <div className="grid h-full place-items-center p-4 text-center text-sm text-slate-400">No conversations yet</div>
          ) : conversations.map((c) => (
            <button
              key={c.id}
              onClick={() => { setActiveId(c.id); setShowThreadMobile(true); }}
              className={cn('flex w-full items-center gap-3 border-b border-slate-100 p-4 text-left hover:bg-slate-50', activeId === c.id && 'bg-blue-50')}
              data-testid={`conversation-${c.id}`}
            >
              <img
                src={avatarUrl(c.other_participant?.full_name || c.other_participant?.email)}
                alt=""
                className="h-10 w-10 rounded-full object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">
                  {c.other_participant?.full_name || c.other_participant?.email || 'Unknown'}
                </p>
                <p className="truncate text-xs text-slate-500">{c.last_message_body}</p>
              </div>
              {c.unread_count > 0 && (
                <span className="grid h-5 min-w-5 place-items-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">
                  {c.unread_count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Thread */}
        <div className={cn('flex flex-col', !showThreadMobile && 'hidden md:flex')}>
          {active ? (
            <>
              <div className="flex items-center gap-3 border-b border-slate-200 p-4">
                <button className="md:hidden" onClick={() => setShowThreadMobile(false)} aria-label="Back">
                  <ChevronLeft size={20} />
                </button>
                <img src={avatarUrl(active.other_participant?.full_name || active.other_participant?.email)} alt="" className="h-9 w-9 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {active.other_participant?.full_name || active.other_participant?.email}
                  </p>
                </div>
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4">
                {msgsLoading ? (
                  <div className="space-y-2">{[1, 2, 3].map((i) => <SkeletonCard key={i} />)}</div>
                ) : messages.map((msg) => (
                  <div key={msg.id} className={cn('flex', msg.is_mine ? 'justify-end' : 'justify-start')}>
                    <div className={cn('max-w-[75%] rounded-2xl px-4 py-2 text-sm', msg.is_mine ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 border border-slate-200')}>
                      {msg.body}
                      <span className={cn('mt-1 block text-[10px]', msg.is_mine ? 'text-white/70' : 'text-slate-400')}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <form onSubmit={handleSend} className="flex items-center gap-2 border-t border-slate-200 p-3">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a message..."
                  className="rounded-full"
                  data-testid="message-input"
                />
                <Button type="submit" size="icon" className="shrink-0 rounded-full" disabled={sendMut.isPending} data-testid="message-send">
                  <Send size={16} />
                </Button>
              </form>
            </>
          ) : (
            <div className="grid flex-1 place-items-center text-sm text-slate-400">Select a conversation</div>
          )}
        </div>
      </div>
    </div>
  );
}
